import { deepEqual } from 'fast-equals';
import React, {
  createContext,
  DependencyList,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import { useDragContext } from '../drag/DragContext';

import styles from './DndContext.module.css';
import DndManager, {
  DndDragEventPayload,
  DndItem,
  DndType,
  DragSourceId,
  DropTargetId,
} from './DndManager';

export interface DndContextValue {
  getDndManager: () => DndManager;
  activeDrag?: DndDragEventPayload<DndType>;
}

const DndContext = createContext<DndContextValue>({
  getDndManager: () => {
    throw new Error();
  },
});

export function DndContextProvider({ children }: PropsWithChildren<{}>) {
  const { getDragManager } = useDragContext();

  const managerRef = useRef(new DndManager(getDragManager()));

  const getDndManager = useCallback(() => managerRef.current, []);

  const [activeDrag, setActiveDrag] = useState<DndDragEventPayload<DndType>>();

  useEffect(() => {
    const manager = managerRef.current;
    manager.init();

    const onDragStartOrMove = (event: DndDragEventPayload<DndType>) => {
      setActiveDrag((drg) => (deepEqual(drg, event) ? drg : event));
    };
    const onDragEnd = (): void => {
      setActiveDrag(undefined);
    };

    manager.on('dragStart', onDragStartOrMove);
    manager.on('dragMove', onDragStartOrMove);
    manager.on('dragEnd', onDragEnd);

    return () => {
      manager.off('dragStart', onDragStartOrMove);
      manager.off('dragMove', onDragStartOrMove);
      manager.off('dragEnd', onDragEnd);
      manager.close();
    };
  }, []);

  return (
    <DndContext.Provider value={{ getDndManager, activeDrag }}>{children}</DndContext.Provider>
  );
}

export function useDndDrag<T extends DndType, E extends HTMLElement>(
  optionsFactory: () => {
    id: DragSourceId;
    type: T;
    item: DndItem<T> | undefined;
  },
  deps: DependencyList,
): {
  dragRef: (el: E | null) => void;
  DragPlaceholder: (
    props: React.PropsWithChildren<{ size: { width: number; height: number } }>,
  ) => JSX.Element | null;
} {
  const { getDndManager } = useContext(DndContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const options = useMemo(optionsFactory, deps);

  const { setPlaceholderPosition, DragPlaceholder, getPlaceholderSize } = useDragPlaceholder();

  const previousIdRef = useRef<DragSourceId>();

  const dragRef = useCallback(
    (el: E | null) => {
      const dndManager = getDndManager();
      if (el) {
        dndManager.addDragSource(options.id, {
          type: options.type,
          item: options.item,
          el,
          setPlaceholderPosition,
          getPlaceholderSize,
        });
        if (previousIdRef.current && previousIdRef.current !== options.id) {
          dndManager.removeDragSource(previousIdRef.current);
        }
      } else if (previousIdRef.current) {
        dndManager.removeDragSource(previousIdRef.current);
      }
      previousIdRef.current = options.id;
    },
    [
      getDndManager,
      getPlaceholderSize,
      options.id,
      options.item,
      options.type,
      setPlaceholderPosition,
    ],
  );

  return { dragRef, DragPlaceholder };
}

function useDragPlaceholder() {
  const portalRoot = useMemo(() => {
    const el = document.createElement('div');
    el.className = styles.DragPlaceholderPortal;
    return el;
  }, []);
  useEffect(() => {
    document.body.appendChild(portalRoot);
    return () => {
      document.body.removeChild(portalRoot);
    };
  }, [portalRoot]);
  const Portal = useCallback(
    (props: PropsWithChildren<{}>) => ReactDOM.createPortal(props.children, portalRoot),
    [portalRoot],
  );

  const sizeRef = useRef<{ width: number; height: number }>();
  const getPlaceholderSize = useCallback(() => sizeRef.current, []);

  const [placeholderPosition, setPlaceholderPosition] = useState<{ x: number; y: number }>();

  const DragPlaceholder = useCallback(
    (props: PropsWithChildren<{ size: { width: number; height: number } }>) => {
      sizeRef.current = props.size;
      if (!placeholderPosition) {
        return null;
      }
      return (
        <Portal>
          <div
            className={styles.DragPlaceholderContainer}
            style={{
              transform: `translateX(${placeholderPosition.x}px) translateY(${placeholderPosition.y}px)`,
              width: `${props.size.width}px`,
              height: `${props.size.height}px`,
            }}
          >
            {props.children}
          </div>
        </Portal>
      );
    },
    [Portal, placeholderPosition],
  );

  return { DragPlaceholder, setPlaceholderPosition, getPlaceholderSize };
}

export function useDndDrop<T extends DndType, E extends HTMLElement>(
  optionsFactory: () => {
    id: DropTargetId;
    accept: T;
    canDrop: (item: DndItem<T>) => boolean;
    onDrop: (item: DndItem<T>) => void;
  },
  deps: DependencyList,
): {
  dropRef: (el: E | null) => void;
  canDrop: boolean;
  /**
   * The current drag operation is over this drop target. This doesn't necessarily indicate that the
   * user can drop here, consult `canDrop` for that!
   */
  isOver: boolean;
} {
  const { getDndManager, activeDrag } = useContext(DndContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const options = useMemo(optionsFactory, deps);

  const previousIdRef = useRef<DropTargetId>();

  const dropRef = useCallback(
    (el: E | null) => {
      const dndManager = getDndManager();
      if (el) {
        dndManager.addDropTarget(options.id, {
          accept: options.accept,
          el,
          canDrop: options.canDrop,
          onDrop: options.onDrop,
        });
        if (previousIdRef.current && previousIdRef.current !== options.id) {
          dndManager.removeDropTarget(previousIdRef.current);
        }
      } else if (previousIdRef.current) {
        dndManager.removeDropTarget(previousIdRef.current);
      }
      previousIdRef.current = options.id;
    },
    [getDndManager, options],
  );

  const { canDrop, isOver } = useMemo(() => {
    return {
      canDrop:
        !!activeDrag && activeDrag.type === options.accept && options.canDrop(activeDrag.item),
      isOver: !!activeDrag?.overTargetId && options.id === activeDrag.overTargetId,
    };
  }, [activeDrag, options]);

  return { dropRef, canDrop, isOver };
}
